import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, User, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  AuthLayout,
  AuthCard,
  AuthHeader,
  Button,
  Input,
  FormField,
  PasswordInput,
  Alert,
  BusinessBadge
} from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import axios from 'axios';

const InvitationAcceptance = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [step, setStep] = useState('loading'); // loading, form, success, error
  const [invitationData, setInvitationData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const token = searchParams.get('token');
  
  // Load invitation details on mount
  useEffect(() => {
    if (!token) {
      setStep('error');
      setError('Invalid invitation link - missing token');
      return;
    }
    
    loadInvitationDetails();
  }, [token]);
  
  const loadInvitationDetails = async () => {
    try {
      console.log('🔍 Loading invitation details for token:', token);
      
      // Hardcoded backend URL for now
      const backendUrl = 'https://zoiks-api-756775126757.us-central1.run.app/api';
      const fullUrl = `${backendUrl}/v2/public/invitation/${token}`;
      console.log('🔍 Making request to:', fullUrl);
      
      const response = await axios.get(fullUrl);
      console.log('✅ API Response:', response);
      console.log('✅ Response data:', response.data);
      
      setInvitationData(response.data.data);
      setStep('form');
    } catch (err) {
      console.error('❌ API Error:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error data:', err.response?.data);
      
      setStep('error');
      setError(err.response?.data?.message || 'Failed to load invitation details');
    }
  };
  
  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setError(''); // Clear error when user types
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      // Hardcoded backend URL for now
      const backendUrl = 'https://zoiks-api-756775126757.us-central1.run.app/api';
      const fullUrl = `${backendUrl}/v2/public/invitation/${token}/accept`;
      
      const response = await axios.post(fullUrl, {
        name: formData.name,
        password: formData.password
      });
      
      // Auto-login with the received token
      const { access_token } = response.data.data;
      localStorage.setItem('token', access_token);
      
      setStep('success');
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Loading state
  if (step === 'loading') {
    return (
      <AuthLayout>
        <AuthCard>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-slate-400">Loading invitation...</p>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }
  
  // Error state
  if (step === 'error') {
    return (
      <AuthLayout>
        <AuthCard>
          <div className="text-center py-8">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Invalid Invitation</h2>
            <p className="text-slate-400 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} variant="secondary">
              Go to Login
            </Button>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }
  
  // Success state
  if (step === 'success') {
    return (
      <AuthLayout>
        <AuthCard>
          <div className="text-center py-8">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Welcome to the Team!</h2>
            <p className="text-slate-400 mb-6">Your account has been created successfully. Redirecting to dashboard...</p>
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500 mx-auto"></div>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }
  
  // Form state - 🚨 THIS IS WHERE THE FIXES ARE 🚨
  return (
    <AuthLayout>
      <AuthCard>
        <AuthHeader
          icon={Shield}
          title="Join the Team"
          subtitle="Complete your account setup"
        />
        
        {/* Invitation Details */}
        <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <h3 className="text-sm font-medium text-slate-300 mb-2">You've been invited to join:</h3>
          <BusinessBadge 
            businessName={invitationData?.business?.name || 'Loading...'}
            size="lg"
            className="mb-3"
          />
          <div className="space-y-1 text-sm text-slate-400">
            <p><span className="text-slate-300">Role:</span> {invitationData?.invitation?.role}</p>
            <p><span className="text-slate-300">Invited by:</span> {invitationData?.invited_by?.name}</p>
            <p><span className="text-slate-300">Email:</span> {invitationData?.invitation?.email}</p>
          </div>
          {invitationData?.invitation?.message && (
            <div className="mt-3 p-3 bg-blue-900/20 border border-blue-700/30 rounded">
              <p className="text-sm text-blue-200">"{invitationData.invitation.message}"</p>
            </div>
          )}
        </div>
        
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormField label="Full Name" required>
            <Input
              type="text"
              value={formData.name}
              onChange={handleInputChange('name')}
              placeholder="Enter your full name"
              leftIcon={User}
              required
              disabled={isSubmitting}
            />
          </FormField>
          
          <FormField label="Password" required>
            <PasswordInput
              value={formData.password}
              onChange={handleInputChange('password')}
              placeholder="Create a password (min 8 characters)"
              required
              disabled={isSubmitting}
            />
          </FormField>
          
          <FormField label="Confirm Password" required>
            <PasswordInput
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              placeholder="Confirm your password"
              required
              disabled={isSubmitting}
            />
          </FormField>
          
          <Button
            type="submit"
            variant="primary"
            size="md"
            isLoading={isSubmitting}
            disabled={!formData.name || !formData.password || !formData.confirmPassword || isSubmitting}
            className="w-full"
          >
            {isSubmitting ? 'Creating Account...' : 'Accept Invitation & Create Account'}
          </Button>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

export default InvitationAcceptance;